import {
  Box,
  Grid,
  Toolbar,
  Typography,
  CircularProgress,
  Button,
  Pagination,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../../utils/Api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import {
  CloudDownloadOutlined,
  Visibility,
  WarningAmberRounded,
  CheckCircle,
  FilterAltOutlined,
} from "@mui/icons-material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { toast } from "react-toastify";

function OrdersReport() {
  const token = localStorage.getItem("token");
  const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");

  const outletId =
    outletData?._id ||
    outletData?.id ||
    outletData?.outletId ||
    outletData?.outlet?._id ||
    "";

  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [selectedStartDate, setSelectedStartDate] = useState(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState(new Date());
  const [searchText, setSearchText] = useState("");
  const [orderReports, setOrderReports] = useState([]);
  const [orderReportsData, setOrderReportsData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const itemsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [serverTotalPages, setServerTotalPages] = useState(1);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const asOrderRow = (order) => ({ order, orderItems: order?.orderItems || [] });

  const extractRowsFromListResponse = (data) => {
    const rows =
      data?.orders ||
      data?.totalOrdersData ||
      data?.orderReports ||
      data?.records ||
      [];

    return Array.isArray(rows)
      ? rows.map((row) => (row?.order ? row : asOrderRow(row)))
      : [];
  };

  const extractDetailsFromResponse = (responseData) => {
    const data = responseData?.data || responseData || {};
    const order = data?.order || data?.newOrder || data?.selectedOrder || data;

    return {
      order,
      orderItems: data?.orderItems || order?.orderItems || [],
      orderStatusLogs: data?.orderStatusLogs || order?.orderStatusLogs || [],
    };
  };

  const getErrorMessage = (error, fallback) => {
    const res = error?.response;
    return (
      res?.data?.error?.message ||
      res?.data?.data?.message ||
      res?.data?.error ||
      res?.data?.message ||
      fallback
    );
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedOrder(null);
  };

  const normalizeArrivalMeta = (orderObj) => ({
    status: orderObj?.order?.orderArrivalStatus || null,
    earlyArrival: orderObj?.order?.earlyArrival || null,
    delayedArrival: orderObj?.order?.delayedArrival || null,
  });

  const getDelayedChipProps = (orderObj) => {
    const { status } = normalizeArrivalMeta(orderObj);
    return status === "DELAYED" ? { label: "Late" } : null;
  };

  const getArrivalChipPropsForModal = (orderObj) => {
    const { status, earlyArrival, delayedArrival } = normalizeArrivalMeta(orderObj);

    if (status === "EARLY") {
      return {
        label: `EARLY${earlyArrival ? ` • ${earlyArrival}` : ""}`,
        color: "#15803d",
        bg: "#dcfce7",
        border: "#86efac",
        icon: <CheckCircle sx={{ fontSize: 16 }} />,
      };
    }

    if (status === "DELAYED") {
      return {
        label: `DELAYED${delayedArrival ? ` • ${delayedArrival}` : ""}`,
        color: "#b91c1c",
        bg: "#fee2e2",
        border: "#fca5a5",
        icon: <WarningAmberRounded sx={{ fontSize: 16 }} />,
      };
    }

    return null;
  };

  const getStatusChipStyles = (status) => {
    switch (status) {
      case "COMPLETED":
      case "DELIVERED":
        return { bg: "#dcfce7", color: "#166534" };
      case "CONFIRMED":
        return { bg: "#dbeafe", color: "#1d4ed8" };
      case "CANCELLED":
        return { bg: "#fee2e2", color: "#b91c1c" };
      case "READY-TO-PICK":
        return { bg: "#f3e8ff", color: "#7e22ce" };
      default:
        return { bg: "#e2e8f0", color: "#334155" };
    }
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) return "N/A";
    const d = new Date(dateValue);
    return `${d.toLocaleDateString()}, ${d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })}`;
  };

  const getOrderReports = async (download = false, page = 1) => {
    if (!outletId) {
      toast.error("Outlet details not found. Please login again.", { autoClose: 3000 });
      return;
    }

    if (download) setDownloadLoading(true);
    else setLoading(true);

    try {
      const startDate = format(selectedStartDate, "yyyy-MM-dd");
      const endDate = format(selectedEndDate, "yyyy-MM-dd");

      const params = {
        startDate,
        endDate,
        page: String(page),
        limit: String(itemsPerPage),
      };

      if (searchText.trim()) params.search = searchText.trim();
      if (download) params.isDownload = "true";

      const response = await axios({
        method: "POST",
        url: `${API.BASE_URL}order/getOrdersForAdmin/new`,
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        params,
        data: { outletIds: [outletId] },
        responseType: download ? "blob" : "json",
      });

      if (download) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        const outletName = outletData?.name || outletData?.outletName || "Outlet";
        link.download = `${outletName} Orders Report (${startDate} to ${endDate}).xlsx`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);

        toast.success("Report Excel file downloaded successfully!", {
          position: "bottom-center",
          autoClose: 3000,
        });

        return;
      }

      const data = response?.data?.data || {};
      const rows = extractRowsFromListResponse(data);
      const paginationData = data?.paginationData || {};

      setOrderReportsData(data);
      setSummaryData(data?.totalSummaryData || null);
      setOrderReports(rows);
      setCurrentPage(Number(paginationData?.currentPage || page));
      setServerTotalPages(Number(paginationData?.totalPages || 1));
    } catch (error) {
      console.error("Orders report error:", error);
      toast.error(getErrorMessage(error, download ? "Failed to download report" : "Failed to fetch report"), {
        autoClose: 3000,
      });

      if (!download) {
        setOrderReports([]);
        setOrderReportsData(null);
        setSummaryData(null);
        setServerTotalPages(1);
      }
    } finally {
      if (download) setDownloadLoading(false);
      else setLoading(false);
    }
  };

  const getOrderDetails = async (orderId) => {
    if (!orderId) return;

    setOpenModal(true);
    setSelectedOrder(null);
    setDetailsLoading(true);

    try {
      const response = await axios.get(`${API.BASE_URL}order/get-order-details/${orderId}`, {
        headers: authHeaders,
      });

      setSelectedOrder(extractDetailsFromResponse(response?.data));
    } catch (error) {
      console.error("Order details error:", error);
      toast.error(getErrorMessage(error, "Failed to fetch order details"), { autoClose: 3000 });
      setOpenModal(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const rows = Array.isArray(orderReports) ? orderReports : [];
    const q = searchText.trim().toLowerCase();

    if (!q) return rows;

    return rows.filter((r) => {
      const orderNo = `#${r?.order?.order_sequence || ""}${r?.order?.order_no || ""}`.toLowerCase();
      const name = (
        r?.order?.kioskUser?.name ||
        r?.order?.outletUser?.name ||
        r?.order?.user?.name ||
        ""
      ).toLowerCase();
      const phone = (
        r?.order?.kioskUser?.phone ||
        r?.order?.kioskUser?.mobile ||
        r?.order?.outletUser?.mobile ||
        r?.order?.user?.mobile ||
        ""
      ).toLowerCase();
      const coupon = (r?.order?.coupon?.couponCode || "").toLowerCase();
      const source = (r?.order?.source || "").toLowerCase();
      const arrivalStatus = (r?.order?.orderArrivalStatus || "").toLowerCase();

      return (
        orderNo.includes(q) ||
        name.includes(q) ||
        phone.includes(q) ||
        coupon.includes(q) ||
        source.includes(q) ||
        arrivalStatus.includes(q)
      );
    });
  }, [orderReports, searchText]);

  const legendTotals = useMemo(() => {
    if (summaryData) {
      return {
        totalOrders: Number(summaryData?.totalOrderCount || filteredRows.length || 0),
        totalGrand: Number(summaryData?.grandTotalAmount || 0),
        totalTax: Number(summaryData?.totalTaxAmount || 0),
        totalDiscount: Number(summaryData?.totalDiscountAmount || 0),
        earlyCount: filteredRows.filter((r) => r?.order?.orderArrivalStatus === "EARLY").length,
        delayedCount: filteredRows.filter((r) => r?.order?.orderArrivalStatus === "DELAYED").length,
      };
    }

    return filteredRows.reduce(
      (acc, r) => {
        const arrivalStatus = r?.order?.orderArrivalStatus;

        acc.totalOrders += 1;
        acc.totalGrand += Number(r?.order?.grandTotal || 0);
        acc.totalTax += Number(r?.order?.totalTaxAmount || 0);
        acc.totalDiscount += Number(r?.order?.totalDiscount || 0);

        if (arrivalStatus === "EARLY") acc.earlyCount += 1;
        if (arrivalStatus === "DELAYED") acc.delayedCount += 1;

        return acc;
      },
      {
        totalOrders: 0,
        totalGrand: 0,
        totalTax: 0,
        totalDiscount: 0,
        earlyCount: 0,
        delayedCount: 0,
      }
    );
  }, [filteredRows, summaryData]);

  const totalPages = serverTotalPages || Math.ceil((filteredRows.length || 0) / itemsPerPage) || 1;
  const paginatedRows = serverTotalPages > 1 ? filteredRows : filteredRows.slice(0, itemsPerPage);

  const getOrderStatusStyles = (status) => {
    switch ((status || "").toUpperCase()) {
      case "CONFIRMED":
        return {
          color: "#1D4ED8",
          bgColor: "#DBEAFE",
          borderColor: "#93C5FD",
        };

      case "COMPLETED":
        return {
          color: "#15803D",
          bgColor: "#DCFCE7",
          borderColor: "#86EFAC",
        };

      case "DELIVERED":
        return {
          color: "#047857",
          bgColor: "#D1FAE5",
          borderColor: "#6EE7B7",
        };

      case "CANCELLED":
        return {
          color: "#B91C1C",
          bgColor: "#FEE2E2",
          borderColor: "#FCA5A5",
        };

      default:
        return {
          color: "#475569",
          bgColor: "#F1F5F9",
          borderColor: "#CBD5E1",
        };
    }
  };

  useEffect(() => {
    getOrderReports(false, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className="dashBoard" sx={{ background: "#f6f8fb", minHeight: "100vh" }}>
      <Toolbar />

      <Grid container justifyContent="center" sx={{ pb: 6 }}>
        <Grid item xs={12} md={11}>
          <Card
            elevation={0}
            sx={{
              mt: 4,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                    Customer Orders Report
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Filter by date, search orders, then view or download the report.
                  </Typography>
                </Box>

                <Chip
                  icon={<FilterAltOutlined />}
                  label={`Records: ${summaryData?.totalOrderCount || filteredRows.length || 0}`}
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                  variant="outlined"
                />
              </Stack>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ mt: 2, borderRadius: 3, border: "1px solid #e6eaf0", background: "#fff" }}>
            <CardContent sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                <Grid item xs={12} md={2} sx={{ zIndex: 1000 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Start Date
                  </Typography>
                  <DatePicker
                    selected={selectedStartDate}
                    onChange={(date) => {
                      setSelectedStartDate(date);
                      setCurrentPage(1);
                    }}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={
                      <TextField
                        fullWidth
                        value={selectedStartDate ? format(selectedStartDate, "yyyy-MM-dd") : ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 52,
                            background: "#fff",
                          },
                        }}
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12} md={2} sx={{ zIndex: 1000 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    End Date
                  </Typography>
                  <DatePicker
                    selected={selectedEndDate}
                    onChange={(date) => {
                      setSelectedEndDate(date);
                      setCurrentPage(1);
                    }}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    minDate={selectedStartDate}
                    customInput={
                      <TextField
                        fullWidth
                        value={selectedEndDate ? format(selectedEndDate, "yyyy-MM-dd") : ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 52,
                            background: "#fff",
                          },
                        }}
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Search Order
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Search by order no / customer / coupon / arrival status"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      setCurrentPage(1);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") getOrderReports(false, 1);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        height: 52,
                        background: "#fff",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#334155",
                      mb: 0.8,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Typography>

                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Button
                      variant="contained"
                      disabled={loading || downloadLoading}
                      onClick={() => getOrderReports(false, 1)}
                      sx={{
                        height: 52,
                        width: "70%",
                        borderRadius: 2,
                        fontWeight: 800,
                        textTransform: "none",
                        backgroundColor: "#003049",
                        "&:hover": { backgroundColor: "#00263a" },
                      }}
                    >
                      {loading ? "Loading..." : "Get"}
                    </Button>

                    {downloadLoading ? (
                      <CircularProgress size={32} sx={{ alignSelf: "center", color: "#003049" }} />
                    ) : (
                      <CloudDownloadOutlined
                        onClick={() => filteredRows.length > 0 && getOrderReports(true, currentPage)}
                        sx={{
                          fontSize: "35px",
                          cursor: filteredRows.length > 0 ? "pointer" : "not-allowed",
                          color: filteredRows.length > 0 ? "#003049" : "#94a3b8",
                          display: "flex",
                          alignSelf: "center",
                        }}
                      />
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  <CircularProgress />
                  <Typography sx={{ color: "#334155", fontWeight: 700 }}>Fetching Orders...</Typography>
                </Box>
              ) : filteredRows.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                    No orders to display
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Select a date and click “Get” to view results.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0", background: "#fff" }}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip icon={<FilterAltOutlined />} label={`Rows: ${summaryData?.totalOrderCount || filteredRows.length}`} variant="outlined" sx={{ fontWeight: 800, borderRadius: 2 }} />
                        <Chip label={`Early: ${legendTotals.earlyCount}`} sx={{ fontWeight: 800, borderRadius: 2, color: "#166534", backgroundColor: "#dcfce7", border: "1px solid #86efac" }} />
                        <Chip label={`Delayed: ${legendTotals.delayedCount}`} sx={{ fontWeight: 800, borderRadius: 2, color: "#b91c1c", backgroundColor: "#fee2e2", border: "1px solid #fca5a5" }} />
                        <Chip label={`Tax: ₹${legendTotals.totalTax.toFixed(2)}`} variant="outlined" sx={{ fontWeight: 800, borderRadius: 2 }} />
                        <Chip label={`Discount: ₹${legendTotals.totalDiscount.toFixed(2)}`} variant="outlined" sx={{ fontWeight: 800, borderRadius: 2 }} />
                      </Stack>

                      <Chip
                        label={`Grand Total: ₹${legendTotals.totalGrand.toFixed(2)}`}
                        sx={{
                          fontWeight: 900,
                          borderRadius: 2,
                          backgroundColor: "#003049",
                          color: "#fff",
                          "& .MuiChip-label": { color: "#fff" },
                        }}
                      />
                    </Stack>
                  </Box>

                  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, maxHeight: "70vh", overflow: "auto" }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {["Order No", "Customer", "Date", "Time", "Amount", "Detail"].map((h) => (
                            <TableCell
                              key={h}
                              align={h === "Customer" ? "left" : "center"}
                              sx={{
                                fontWeight: 900,
                                color: "#0f172a",
                                background: "#f1f5f9",
                                borderBottom: "1px solid #e2e8f0",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                              }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {paginatedRows.map((row, index) => {
                          const order = row?.order || {};
                          const orderNo = `#${order?.order_sequence || ""}${order?.order_no || ""}`;
                          const custName = order?.kioskUser?.name || order?.outletUser?.name || order?.user?.name;
                          const custPhone = order?.kioskUser?.phone || order?.kioskUser?.mobile || order?.outletUser?.mobile || order?.user?.mobile;
                          const createdAt = order?.createdAt ? new Date(order.createdAt) : null;
                          const delayedChip = getDelayedChipProps(row);

                          return (
                            <TableRow
                              key={(order?._id || orderNo) + index}
                              hover
                              sx={{
                                backgroundColor: index % 2 === 0 ? "#ffffff" : "#EDF9FF",
                                "& td": { borderBottom: "1px solid #f1f5f9" },
                              }}
                            >
                              <TableCell align="center" sx={{ fontWeight: 800, color: "#0f172a" }}>
                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", justifyContent: "center" }}>
                                  <Typography component="span" sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                                    {orderNo}
                                  </Typography>

                                  {delayedChip && (
                                    <Chip
                                      label="Late"
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: "10px",
                                        fontWeight: 800,
                                        borderRadius: "10px",
                                        color: "#b91c1c",
                                        backgroundColor: "#fee2e2",
                                        border: "1px solid #fca5a5",
                                        "& .MuiChip-label": { px: 0.8, py: 0 },
                                      }}
                                    />
                                  )}
                                </Box>
                              </TableCell>

                              <TableCell align="left">
                                <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#0f172a", textTransform: "capitalize" }}>
                                  {custName || "N/A"}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                                  {custPhone || "N/A"}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: "#475569", mt: 0.5 }}>
                                  <Box
                                    component="span"
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      px: 1,
                                      py: 0.3,
                                      borderRadius: "12px",
                                      fontSize: "11px",
                                      fontWeight: 800,
                                      textTransform: "uppercase",
                                      ...(() => {
                                        const statusStyle = getOrderStatusStyles(order?.orderStatus);
                                        return {
                                          color: statusStyle.color,
                                          backgroundColor: statusStyle.bgColor,
                                          border: `1px solid ${statusStyle.borderColor}`,
                                        };
                                      })(),
                                    }}
                                  >
                                    {order?.orderStatus || "N/A"}
                                  </Box>
                                </Typography>
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 700 }}>
                                {createdAt ? format(createdAt, "yyyy-MM-dd") : "N/A"}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 700 }}>
                                {createdAt
                                  ? createdAt.toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })
                                  : "N/A"}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 900, color: "#0f172a" }}>
                                ₹{Number(order?.grandTotal || 0).toFixed(2)}
                              </TableCell>

                              <TableCell align="center">
                                <IconButton onClick={() => getOrderDetails(order?._id)}>
                                  <Visibility />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider />

                  <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(event, page) => getOrderReports(false, page)}
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 900, py: 1.4 }}>
          Order Details
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          {detailsLoading ? (
            <Box sx={{ py: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
              <CircularProgress size={24} />
              <Typography sx={{ color: "#334155", fontWeight: 700 }}>Fetching Order Details...</Typography>
            </Box>
          ) : selectedOrder ? (
            <Grid container spacing={1.3}>
              {(() => {
                const arrivalChip = getArrivalChipPropsForModal(selectedOrder);

                return (
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" justifyContent="center" sx={{ mb: 0.8 }}>
                      <Chip size="small" label={`Order #${selectedOrder?.order?.order_sequence || ""}${selectedOrder?.order?.order_no || ""}`} sx={{ fontWeight: 900, backgroundColor: "#003049", color: "#fff" }} />

                      <Chip size="small" label={selectedOrder?.order?.orderStatus || "N/A"} sx={{ fontWeight: 800, backgroundColor: getStatusChipStyles(selectedOrder?.order?.orderStatus).bg, color: getStatusChipStyles(selectedOrder?.order?.orderStatus).color }} />

                      <Chip size="small" label={selectedOrder?.order?.paymentStatus || "N/A"} sx={{ fontWeight: 800, backgroundColor: selectedOrder?.order?.paymentStatus === "PAID" ? "#dcfce7" : "#fee2e2", color: selectedOrder?.order?.paymentStatus === "PAID" ? "#166534" : "#b91c1c" }} />

                      {arrivalChip && (
                        <Chip size="small" icon={arrivalChip.icon} label={arrivalChip.label} sx={{ fontWeight: 800, color: arrivalChip.color, backgroundColor: arrivalChip.bg, border: `1px solid ${arrivalChip.border}`, "& .MuiChip-icon": { color: arrivalChip.color } }} />
                      )}
                    </Stack>
                  </Grid>
                );
              })()}

              {[
                ["User Name", selectedOrder?.order?.kioskUser?.name || selectedOrder?.order?.outletUser?.name || selectedOrder?.order?.user?.name || "N/A"],
                ["Mobile Number", selectedOrder?.order?.kioskUser?.phone || selectedOrder?.order?.kioskUser?.mobile || selectedOrder?.order?.outletUser?.mobile || selectedOrder?.order?.user?.mobile || "N/A"],
                ["Coupon Code", selectedOrder?.order?.coupon?.couponCode || "N/A"],
                ["Order Source", selectedOrder?.order?.source || "N/A"],
                ["Order Type", selectedOrder?.order?.ordersType || "N/A"],
                ["Date & Time", formatDateTime(selectedOrder?.order?.createdAt)],
              ].map(([label, value]) => (
                <Grid item md={6} xs={12} key={label}>
                  <Box sx={{ p: 1, border: "1px solid #e5e7eb", borderRadius: 1.5, backgroundColor: "#f8fafc", display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: "#64748b", fontWeight: 700 }}>{label}</Typography>
                    <Typography sx={{ fontSize: 14, color: label === "Coupon Code" ? "green" : "#0f172a", fontWeight: 800 }}>
                      {value}
                    </Typography>
                  </Box>
                </Grid>
              ))}

              <Grid item xs={12} sx={{ mt: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 0.8, textAlign: "center" }}>
                  Ordered Items
                </Typography>

                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f1f5f9" }}>
                      <th style={{ padding: "7px", border: "1px solid #d1d5db" }}>Product</th>
                      <th style={{ padding: "7px", border: "1px solid #d1d5db", textAlign: "center" }}>Qty</th>
                      <th style={{ padding: "7px", border: "1px solid #d1d5db", textAlign: "right" }}>Price</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(selectedOrder?.orderItems || []).map((item, index) => (
                      <tr key={item?._id || index}>
                        <td style={{ padding: "7px", border: "1px solid #e5e7eb", verticalAlign: "top" }}>
                          <strong>{item?.product?.name || "N/A"}</strong>
                          {item?.addons?.length > 0 && (
                            <ul style={{ paddingLeft: "16px", margin: "3px 0 0", fontSize: 12 }}>
                              {item.addons.map((addon, i) => (
                                <li key={addon?._id || i}>{addon?.selectedValue} (₹{addon?.offerPrice})</li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td style={{ padding: "7px", border: "1px solid #e5e7eb", textAlign: "center" }}>{item?.quantity || 0}</td>
                        <td style={{ padding: "7px", border: "1px solid #e5e7eb", textAlign: "right" }}>
                          ₹{Number(item?.taxable ?? item?.rate ?? 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}

                    {[
                      ["Total Tax Amount", selectedOrder?.order?.totalTaxAmount],
                      ["Total Discount", selectedOrder?.order?.totalDiscount],
                      ["Order Total", selectedOrder?.order?.grandTotal],
                    ].map(([label, value]) => (
                      <tr key={label}>
                        <td colSpan={2} style={{ padding: "7px", border: "1px solid #e5e7eb", textAlign: "right", fontWeight: 800 }}>
                          {label}
                        </td>
                        <td style={{ padding: "7px", border: "1px solid #e5e7eb", textAlign: "right", fontWeight: 900 }}>
                          ₹{Number(value || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 0.8, mt: 0.5, textAlign: "center" }}>
                  Order Status Logs
                </Typography>

                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f1f5f9" }}>
                      <th style={{ padding: "7px", border: "1px solid #d1d5db" }}>Status</th>
                      <th style={{ padding: "7px", border: "1px solid #d1d5db" }}>Timestamp</th>
                    </tr>
                  </thead>

                  <tbody>
                    {selectedOrder?.orderStatusLogs?.length > 0 ? (
                      selectedOrder.orderStatusLogs.map((log, index) => (
                        <tr key={index}>
                          <td style={{ padding: "7px", border: "1px solid #e5e7eb", textAlign: "center" }}>
                            {typeof log?.orderStatus === "string" ? log.orderStatus : "N/A"}
                          </td>
                          <td style={{ padding: "7px", border: "1px solid #e5e7eb", textAlign: "center" }}>
                            {formatDateTime(log?.createdAt)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={2} style={{ padding: "10px", border: "1px solid #e5e7eb", textAlign: "center" }}>
                          No logs available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Typography>No details available</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default OrdersReport;
